/**
 * OverviewTab — at-a-glance summary of group health.
 *
 * iter189 — type-aware simplification:
 *   • Sports → TypeOverviewHero (next session) + MySessionHistoryCard
 *             (recent attendance %) + summary fund (no monthly momentum).
 *   • Trip   → TypeOverviewHero (phase banner) + TripBudgetCard + TripPerFamilyCard
 *             (per-family ledger jump-off) — fund summary hidden for clarity.
 *   • Daily  → TypeOverviewHero (monthly spend) + fund summary intact.
 *   • Event  → TypeOverviewHero (budget gauge) + fund summary intact.
 *
 * FundTab(view="summary") accepts an optional `typeConfig` so it can drop
 * widgets that don't apply (monthly momentum on sports, fines leaderboard
 * on trip, etc.). Keeps a single source of truth for the dense summary
 * widgets while letting each type opt-out of what it doesn't need.
 */
import { TabsContent } from '../../components/ui/tabs';
import FundTab from './FundTab';
import TypeOverviewHero from '../../components/TypeOverviewHero';
import RotationPreview from '../../components/RotationPreview';
import LiveSessionBanner from '../../components/LiveSessionBanner';
import TripBudgetCard from '../../components/TripBudgetCard';
import TripPerFamilyCard from '../../components/TripPerFamilyCard';
import TripCurrencyBadge from '../../components/TripCurrencyBadge';
import MySessionHistoryCard from '../../components/MySessionHistoryCard';
import MyMonthCard from '../../components/MyMonthCard';
import WhatsAppMessageLogCard from '../../components/WhatsAppMessageLogCard';
import WhatsAppInboundLogCard from '../../components/WhatsAppInboundLogCard';
import WhatsAppDisputesCard from '../../components/WhatsAppDisputesCard';
import useGroupTypeConfig from '../../hooks/useGroupTypeConfig';
import PlayLeaderChip from '../../components/play/PlayLeaderChip';

export default function OverviewTab({ ctx }) {
  const typeConfig = useGroupTypeConfig(ctx?.group);
  return (
    <TabsContent value="overview" className="space-y-3">
      {/* Sports sticky live RSVP rail — only renders when sessions exist */}
      <LiveSessionBanner variant="group" groupId={ctx?.group?.id} />

      {/* iter230 — Venue payer rotation preview (all members, sports-style groups) */}
      <RotationPreview groupId={ctx?.group?.id} group={ctx?.group} />

      {/* Per-type hero card — picks the right one or no-ops gracefully */}
      <TypeOverviewHero ctx={ctx} typeConfig={typeConfig} />

      {/* Play — King of the Court chip (racket sports) */}
      <PlayLeaderChip group={ctx?.group} onOpen={ctx?.openPlayTab} />

      {/* iter270 — desktop 2-col grid (mobile order unchanged: columns stack) */}
      <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start" data-testid="overview-columns">
      <div className="space-y-3">
      {/* iter258 — Personal month-to-date strip */}
      <MyMonthCard groupId={ctx?.group?.id} currency={ctx?.group?.currency || 'INR'} />

      {/* Phase 2 — Trip-only widgets */}
      <TripCurrencyBadge group={ctx?.group} isAdmin={ctx?.isAdmin} onSetupClick={ctx?.onOpenTripRateModal} />
      <TripBudgetCard ctx={ctx} />
      <TripPerFamilyCard ctx={ctx} typeConfig={typeConfig} />

      {/* Phase 6 — Sports-only attendance history */}
      <MySessionHistoryCard ctx={ctx} typeConfig={typeConfig} />
      </div>

      <div className="space-y-3">
      {/* Dense fund summary — type-aware widget gating done inside FundTab */}
      <FundTab ctx={ctx} view="summary" typeConfig={typeConfig} />

      {/* iter212 — Per-group WhatsApp message log (admin only) */}
      {ctx?.isAdmin && ctx?.group?.id && (
        <WhatsAppMessageLogCard
          apiPath={`/whatsapp/groups/${ctx.group.id}/messages`}
          title="Recent WhatsApp notifications"
          refreshInterval={60000}
          allowRetry={true}
          defaultLimit={30}
          testIdPrefix="wa-msglog-group"
        />
      )}

      {/* iter214 — Per-group inbound WhatsApp messages (admin only) */}
      {ctx?.isAdmin && ctx?.group?.id && (
        <WhatsAppInboundLogCard
          apiPath={`/whatsapp/groups/${ctx.group.id}/inbound`}
          title="Inbound from members"
          refreshInterval={60000}
          defaultLimit={30}
        />
      )}

      {/* iter214 Phase 2 — Per-group disputes (admin only) */}
      {ctx?.isAdmin && ctx?.group?.id && (
        <WhatsAppDisputesCard
          apiPath={`/whatsapp/groups/${ctx.group.id}/disputes`}
          title="Open WhatsApp disputes"
          refreshInterval={60000}
          defaultLimit={30}
        />
      )}
      </div>
      </div>
    </TabsContent>
  );
}
